import React from "react";
import { hexToRgb } from "@libertify.frontend/helpers";
import { Badge, Tooltip } from "@libertify/ui";
import { Flex } from "antd";
import styled from "styled-components";
import { analyticsTheme } from "./analyticsTheme";

export type HeatCell = {
  background: string;
  color: string;
  text: string;
  sub?: string;
  /** Lighter background, e.g. for a cell read from too few answers; the value stays readable. */
  faded?: boolean;
  /** A total over a row or column, outlined. */
  margin?: boolean;
  tooltip?: string;
};

export type HeatRow = {
  key: string;
  header: React.ReactNode;
  /** Full row header text, shown on hover since the header is cut to one line. */
  title?: string;
  cells: (HeatCell | null)[];
};

export type HeatTableProps = {
  columns: string[];
  rows: HeatRow[];
  legend?: { color: string; label: string }[];
};

const FADED_ALPHA = 0.55;
/** Text color of a faded tile whose own text is white (white on a light tile is unreadable). */
const FADED_DARK_TEXT = analyticsTheme.datasetTealDarker;

const fade = (hex: string): string => {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb.join(", ")}, ${FADED_ALPHA})` : hex;
};

const cellStyle = (cell: HeatCell): React.CSSProperties => ({
  background: cell.faded ? fade(cell.background) : cell.background,
  color: cell.faded && cell.color.toLowerCase() === "#ffffff" ? FADED_DARK_TEXT : cell.color,
  boxShadow: cell.margin ? `inset 0 0 0 1px ${analyticsTheme.cardBorder}` : undefined,
});

/** A heatmap as a table of colored tiles; a missing cell reads "–". */
export const HeatTable: React.FC<HeatTableProps> = ({ columns, rows, legend }) => (
  <StyledScroll>
    <StyledTable>
      <thead>
        <tr>
          <th />
          {columns.map((column) => (
            <th key={column}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <StyledRowHeader>
              <StyledHeaderText
                title={row.title ?? (typeof row.header === "string" ? row.header : undefined)}
              >
                {row.header}
              </StyledHeaderText>
            </StyledRowHeader>
            {columns.map((column, i) => {
              const cell = row.cells[i];
              return cell ? (
                <Tooltip key={column} content={cell.tooltip} disabled={!cell.tooltip}>
                  <StyledCell style={cellStyle(cell)}>
                    {cell.text}
                    {cell.sub ? <StyledSub>{cell.sub}</StyledSub> : null}
                  </StyledCell>
                </Tooltip>
              ) : (
                <StyledEmpty key={column}>–</StyledEmpty>
              );
            })}
          </tr>
        ))}
      </tbody>
    </StyledTable>
    {legend ? (
      <Flex wrap gap={14} style={{ marginTop: 10 }}>
        {legend.map((item) => (
          <Badge key={item.label} color={item.color} text={item.label} />
        ))}
      </Flex>
    ) : null}
  </StyledScroll>
);

// ─── Styled components ───

const StyledScroll = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 3px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  th {
    padding: 4px 6px;
    text-align: center;
    white-space: nowrap;
    font-size: 11px;
    font-weight: 600;
    color: ${analyticsTheme.textLabel};
  }
`;

const StyledRowHeader = styled.td`
  max-width: 360px;
  padding: 0 8px 0 0;
  font-size: 11.5px;
  font-weight: 500;
  color: ${analyticsTheme.textBody};
`;

const StyledHeaderText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledCell = styled.td`
  min-width: 76px;
  height: 30px;
  padding: 0 8px;
  text-align: center;
  border-radius: 6px;
  font-weight: 600;
`;

const StyledEmpty = styled(StyledCell)`
  background: ${analyticsTheme.heatEmpty};
  color: ${analyticsTheme.textTick};
  font-weight: 400;
`;

const StyledSub = styled.div`
  font-size: 10px;
  font-weight: 500;
  opacity: 0.8;
`;
